import { useState, useRef, useEffect } from 'react';
import { Box, Typography, Button, MapOutlinedIcon } from 'src/components';

const PREVIEW_HEIGHT = 65;

export default function MobileSwipeableDrawer({ 
  open, 
  footerHeight, 
  previewText, 
  children, 
  addMarginTop = false,
  defaultState = "collapsed", // "collapsed", "half", or "expanded"
  hideMapButton,
  onStateChange,
  onHeightChange
}) {
  const [state, setState] = useState(defaultState);
  const [dragOffset, setDragOffset] = useState(0);
  const [isDragging, setIsDragging] = useState(false);

  const startY = useRef(null);
  const containerRef = useRef(null);
  const contentRef = useRef(null);
  const headerRef = useRef(null);
  const initialScrollTop = useRef(0);
  const touchStartedOnScrollable = useRef(false);
  const touchStartedOnHeader = useRef(false);

  // Heights
  const collapsedHeight = PREVIEW_HEIGHT;
  const expandedHeight = window.innerHeight - footerHeight;
  const halfHeight = expandedHeight * 0.45;

  const isExpanded = state === "expanded";
  const isCollapsed = state === "collapsed";

  const getCurrentBaseHeight = () => {
    switch (state) {
      case "collapsed": return collapsedHeight;
      case "half": return halfHeight;
      case "expanded": return expandedHeight;
      default: return collapsedHeight;
    }
  };

  // === Translate calculation ===
  const getTranslateY = () => {
    const baseTranslate = expandedHeight - getCurrentBaseHeight();
    return isDragging ? baseTranslate - dragOffset : baseTranslate;
  };

  // === Fire onHeightChange whenever visible height changes ===
  useEffect(() => {
    if (onHeightChange) {
      const visibleHeight = expandedHeight - getTranslateY();
      onHeightChange(visibleHeight);
    }
  }, [dragOffset, state, expandedHeight, onHeightChange]);

  if (!open) return null;

  const onTouchStart = (e) => {
    startY.current = e.touches[0].clientY;
    setIsDragging(true);
    touchStartedOnScrollable.current = false;
    touchStartedOnHeader.current = false;

    if (headerRef.current) {
      const headerRect = headerRef.current.getBoundingClientRect();
      const touchY = e.touches[0].clientY;
      if (touchY >= headerRect.top && touchY <= headerRect.bottom) {
        touchStartedOnHeader.current = true;
        return;
      }
    }

    const target = e.target;
    const scrollableParent = target.closest('[data-scrollable], .scroll, .overflow-auto, .overflow-y-auto') || 
                             target.closest('div[style*="overflow"]') ||
                             target.closest('div[style*="overflow-y"]');

    if (scrollableParent && contentRef.current?.contains(scrollableParent)) {
      touchStartedOnScrollable.current = true;
      initialScrollTop.current = scrollableParent.scrollTop;
    } else if (contentRef.current) {
      initialScrollTop.current = contentRef.current.scrollTop;
    }
  };

  const onTouchMove = (e) => {
    if (startY.current === null || !isDragging) return;

    const currentY = e.touches[0].clientY;
    const deltaY = startY.current - currentY;

    if (!touchStartedOnHeader.current && isExpanded && contentRef.current) {
      if (touchStartedOnScrollable.current) {
        const target = e.target;
        const scrollableParent = target.closest('[data-scrollable], .scroll, .overflow-auto, .overflow-y-auto') || 
                                 target.closest('div[style*="overflow"]') ||
                                 target.closest('div[style*="overflow-y"]');
        if (scrollableParent) {
          const scrollTop = scrollableParent.scrollTop;
          const scrollHeight = scrollableParent.scrollHeight;
          const clientHeight = scrollableParent.clientHeight;
          const canScrollUp = scrollTop > 0;
          const canScrollDown = scrollTop < scrollHeight - clientHeight;
          const SCROLL_TOLERANCE = 5;

          if (deltaY < 0 && canScrollUp) { setDragOffset(0); return; }
          if (deltaY > 0 && canScrollDown) { setDragOffset(0); return; }
          if (Math.abs(deltaY) < SCROLL_TOLERANCE) { setDragOffset(0); return; }
        }
      }

      const scrollTop = contentRef.current.scrollTop;
      const scrollHeight = contentRef.current.scrollHeight;
      const clientHeight = contentRef.current.clientHeight;
      const SCROLL_TOLERANCE = 5;
      const startedAtTop = initialScrollTop.current <= 1;
      const startedAtBottom = initialScrollTop.current >= scrollHeight - clientHeight - 1;

      if (deltaY < 0 && scrollTop > 0) { setDragOffset(0); return; }
      if (deltaY > 0 && scrollTop < scrollHeight - clientHeight) { setDragOffset(0); return; }
      if (Math.abs(deltaY) < SCROLL_TOLERANCE || (deltaY < 0 && !startedAtTop) || (deltaY > 0 && !startedAtBottom)) {
        setDragOffset(0);
        return;
      }
    }

    let maxUpDrag, maxDownDrag;
    switch (state) {
      case "collapsed":
        maxUpDrag = expandedHeight - collapsedHeight;
        maxDownDrag = 0;
        break;
      case "half":
        maxUpDrag = expandedHeight - halfHeight;
        maxDownDrag = halfHeight - collapsedHeight;
        break;
      case "expanded":
        maxUpDrag = 0;
        maxDownDrag = expandedHeight - collapsedHeight;
        break;
      default:
        maxUpDrag = 0;
        maxDownDrag = 0;
    }

    let newOffset;
    if (deltaY > 0) {
      newOffset = Math.min(maxUpDrag, deltaY);
    } else {
      newOffset = Math.max(-maxDownDrag, deltaY);
    }

    setDragOffset(newOffset);
  };

  const onTouchEnd = () => {
    if (startY.current === null) return;

    if (Math.abs(dragOffset) > 20) {
      const dragDirection = dragOffset > 0 ? 'up' : 'down';
      const dragDistance = Math.abs(dragOffset);
      const smallDrag = 30;
      const largeDrag = 200;

      if (dragDistance > smallDrag) {
        if (dragDirection === 'up') {
          if (isCollapsed) {
            updateState(dragDistance > largeDrag ? "expanded" : "half");
          } else if (state === "half") {
            updateState("expanded");
          }
        } else {
          if (isExpanded) {
            updateState(dragDistance > largeDrag ? "collapsed" : "half");
          } else if (state === "half") {
            updateState("collapsed");
          }
        }
      }
    }

    setDragOffset(0);
    setIsDragging(false);
    startY.current = null;
    touchStartedOnScrollable.current = false;
    touchStartedOnHeader.current = false;
  };

  const updateState = (newState) => {
    setState(newState);
    onStateChange?.(newState);
  };

  return (
    <Box
      ref={containerRef}
      sx={{
        position: 'fixed',
        left: 0,
        right: 0,
        bottom: `${footerHeight}px`,
        height: expandedHeight,
        backgroundColor: 'white',
        borderTopLeftRadius: isExpanded ? 0 : 30,
        borderTopRightRadius: isExpanded ? 0 : 30,
        boxShadow: '0 -2px 10px rgba(0,0,0,0.15)',
        transform: `translateY(${getTranslateY()}px)`,
        transition: isDragging
          ? 'none'
          : 'transform 0.85s cubic-bezier(0.25, 1, 0.5, 1), border-radius 0.85s cubic-bezier(0.25, 1, 0.5, 1)',
        display: 'flex',
        flexDirection: 'column',
        zIndex: 1,
        overflow: 'hidden'
      }}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
    >
      {/* Preview Header */}
      <Box
        ref={headerRef}
        sx={{
          height: PREVIEW_HEIGHT,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'grab',
          userSelect: 'none',
          backgroundColor: 'white',
          flexShrink: 0,
          zIndex: 9,
          marginTop: isExpanded
            ? `calc(var(--safe-area-top, 0px) + ${addMarginTop ? 60 : 0}px)`
            : '0',
          transition: isDragging ? 'none' : 'margin-top 0.85s cubic-bezier(0.25, 1, 0.5, 1)'
        }}
      >
        <Box sx={{ width: 30, height: 3, borderRadius: 3, backgroundColor: '#ccc', mb: 1 }} />
        <Typography variant="subtitle1" fontWeight={600} sx={{width: '100%'}}>{previewText}</Typography>
      </Box>

      {/* Content */}
      <Box
        ref={contentRef}
        sx={{
          flexGrow: 1,
          overflowY: isExpanded ? 'auto' : 'hidden',
          display: (state === "half" || isExpanded || isDragging) ? 'block' : 'none'
        }}
      >
        {children({ updateState })}
      </Box>

      {/* Map Button */}
      {(isExpanded && !hideMapButton) && (
        <Box
          sx={{
            position: 'absolute',
            bottom: 50,
            width: '100%',
            display: 'flex',
            justifyContent: 'center',
            zIndex: 10,
            opacity: isDragging ? (() => {
              const visibleHeight = expandedHeight - getTranslateY();
              const progress = Math.max(0, Math.min(1, (visibleHeight - collapsedHeight) / (expandedHeight - collapsedHeight)));
              return progress;
            })() : (state === "collapsed" ? 0 : 1),
            transition: isDragging ? 'none' : 'opacity 0.2s ease',
          }}
        >
          <Button
            onClick={() => updateState("collapsed")}
            variant="contained"
            size="small"
            sx={{
              color: '#ffffff',
              borderRadius: 999,
              boxShadow: 1,
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}
          >
            <MapOutlinedIcon/>
            <Typography variant="body2">Map</Typography>
          </Button>
        </Box>
      )}
    </Box>
  );
}